import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, ButtonGroup, Sheet, Typography } from "@mui/joy";
import { useLocation, useNavigate } from "react-router-dom";
import { useAccumulatedViewRows } from "../../../hooks/useAccumulatedViewRows";
import type { CanvasDataSource } from "../../../types";
import type { RenderableElement } from "../CanvasRenderer";
import { useCanvasRuntime } from "../CanvasRuntimeContext";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { openCanvasNavigation } from "../canvasNavigation";

/** Rows loaded automatically before the calendar asks the user to load more. */
export const CALENDAR_AUTO_LOAD_ROWS = 1000;
const MAX_EVENTS_PER_DAY_IN_MONTH = 3;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTH_ABBREVIATIONS = MONTHS.map((month) => month.slice(0, 3));

export interface CalendarConfig {
  date_field?: string;
  end_date_field?: string;
  title_field?: string;
  default_view?: "month" | "week";
  event_click_action?: {
    type?: string;
    config?: Record<string, unknown>;
  } | null;
}

interface Props {
  element: RenderableElement;
  dataSources: CanvasDataSource[];
  canvasId?: string | null;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
}

/** A calendar day as a local date; the time of day is dropped so zones cannot shift the day. */
function parseDay(value: unknown): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const text = String(value).trim();
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const fallback = new Date(text);
  if (Number.isNaN(fallback.getTime())) return null;
  return new Date(
    fallback.getFullYear(),
    fallback.getMonth(),
    fallback.getDate(),
  );
}

const dayKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** The Monday on or before the date. */
const weekStart = (date: Date): Date =>
  addDays(date, -((date.getDay() + 6) % 7));

export function CanvasCalendarRenderer({
  element,
  dataSources,
  canvasId,
}: Props) {
  const outer = element.props as unknown as CalendarConfig & {
    title?: string;
    data_source_alias?: string;
    calendar_config?: CalendarConfig;
  };
  const config: CalendarConfig = { ...outer, ...outer.calendar_config };
  const runtime = useCanvasRuntime();
  const navigate = useNavigate();
  const location = useLocation();
  const authFetch = useAuthFetch();
  const effectiveCanvasId = canvasId ?? runtime?.canvasId ?? null;
  const alias = outer.data_source_alias || "main";
  const source = dataSources.find((candidate) =>
    [
      candidate.id,
      candidate.name,
      (candidate as { alias?: string }).alias,
    ].includes(alias),
  );
  const viewId = source?.view_id ? String(source.view_id) : null;
  const configured = Boolean(
    effectiveCanvasId && viewId && config.date_field && config.title_field,
  );

  const relationshipFilters = useMemo(
    () =>
      runtime?.interactionBus
        .getRuntimeFilters({
          canvasId: effectiveCanvasId,
          elementId: element.id,
          dataSourceAlias: alias,
        })
        .flatMap((entry) => entry.filters) || [],
    [alias, effectiveCanvasId, element.id, runtime],
  );
  const {
    rows,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  } = useAccumulatedViewRows(configured ? viewId : null, {
    canvasId: effectiveCanvasId || undefined,
    filters: relationshipFilters.length ? relationshipFilters : undefined,
    filterLogic: "AND",
  });
  // Load every page up to a ceiling, then ask: events must not be silently dropped.
  useEffect(() => {
    if (hasMore && !isLoadingMore && rows.length < CALENDAR_AUTO_LOAD_ROWS) {
      loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore, rows.length]);

  const [view, setView] = useState<"month" | "week">(
    config.default_view === "week" ? "week" : "month",
  );
  const [cursor, setCursor] = useState(() => new Date());
  const [navigationError, setNavigationError] = useState<string | null>(null);

  const { eventsByDay, skipped } = useMemo(() => {
    const byDay = new Map<string, CalendarEvent[]>();
    let unusable = 0;
    for (const row of rows as Array<Record<string, unknown>>) {
      const start = config.date_field ? parseDay(row[config.date_field]) : null;
      if (!start) {
        unusable += 1;
        continue;
      }
      let end = config.end_date_field
        ? parseDay(row[config.end_date_field])
        : null;
      if (!end || end < start) end = start;
      const event: CalendarEvent = {
        id: String(row._rowId),
        title: config.title_field ? String(row[config.title_field] ?? "") : "",
        start,
        end,
      };
      // cap one event's span so a wrong end date cannot stall the render
      const span = Math.min(
        Math.round((end.getTime() - start.getTime()) / 86_400_000),
        366,
      );
      for (let offset = 0; offset <= span; offset += 1) {
        const key = dayKey(addDays(start, offset));
        byDay.set(key, [...(byDay.get(key) || []), event]);
      }
    }
    return { eventsByDay: byDay, skipped: unusable };
  }, [config.date_field, config.end_date_field, config.title_field, rows]);

  const days = useMemo(() => {
    if (view === "week") {
      const first = weekStart(cursor);
      return Array.from({ length: 7 }, (_, index) => addDays(first, index));
    }
    const monthFirst = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const monthLast = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const first = weekStart(monthFirst);
    const count =
      Math.ceil(
        ((monthLast.getTime() - first.getTime()) / 86_400_000 + 1) / 7,
      ) * 7;
    return Array.from({ length: count }, (_, index) => addDays(first, index));
  }, [cursor, view]);

  const label =
    view === "month"
      ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
      : `${days[0].getDate()} ${MONTH_ABBREVIATIONS[days[0].getMonth()]} - ${days[6].getDate()} ${
          MONTH_ABBREVIATIONS[days[6].getMonth()]
        } ${days[6].getFullYear()}`;

  const step = useCallback(
    (direction: -1 | 1) =>
      setCursor((current) =>
        view === "month"
          ? new Date(current.getFullYear(), current.getMonth() + direction, 1)
          : addDays(current, 7 * direction),
      ),
    [view],
  );

  const openEvent = useCallback(
    (rowId: string) => {
      const action = config.event_click_action;
      if (!action || action.type !== "NAVIGATE" || !runtime) return;
      setNavigationError(null);
      void openCanvasNavigation(action.config as never, {
        workspaceId: runtime.workspaceId,
        currentRowId: runtime.rowId,
        selectedRowId: rowId,
        returnPath: location.pathname + location.search,
        authFetch,
        navigate,
      }).catch((failure: unknown) => {
        setNavigationError(
          failure instanceof Error
            ? failure.message
            : "Destination unavailable or access denied.",
        );
      });
    },
    [
      authFetch,
      config.event_click_action,
      location.pathname,
      location.search,
      navigate,
      runtime,
    ],
  );
  const clickable = config.event_click_action?.type === "NAVIGATE" && !!runtime;
  const todayKey = dayKey(new Date());

  return (
    <Sheet
      variant="outlined"
      sx={{ p: 2, borderRadius: "sm", height: "100%", overflow: "auto" }}
    >
      {outer.title && (
        <Typography level="title-md" sx={{ mb: 1 }}>
          {outer.title}
        </Typography>
      )}
      {!configured ? (
        <Typography level="body-sm">
          Configure a view, date field and title field for this calendar.
        </Typography>
      ) : error ? (
        <Typography role="alert" level="body-sm">
          Unable to load calendar.
        </Typography>
      ) : isLoading ? (
        <Typography level="body-sm">Loading calendar…</Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <ButtonGroup size="sm" variant="outlined">
              <Button aria-label="Previous" onClick={() => step(-1)}>
                ‹
              </Button>
              <Button onClick={() => setCursor(new Date())}>Today</Button>
              <Button aria-label="Next" onClick={() => step(1)}>
                ›
              </Button>
            </ButtonGroup>
            <Typography level="title-sm" sx={{ flex: 1 }}>
              {label}
            </Typography>
            <ButtonGroup size="sm" variant="outlined">
              <Button
                variant={view === "month" ? "solid" : "outlined"}
                onClick={() => setView("month")}
              >
                Month
              </Button>
              <Button
                variant={view === "week" ? "solid" : "outlined"}
                onClick={() => setView("week")}
              >
                Week
              </Button>
            </ButtonGroup>
          </Box>
          {navigationError && (
            <Typography
              role="alert"
              level="body-sm"
              color="danger"
              sx={{ mb: 1 }}
            >
              {navigationError}
            </Typography>
          )}
          <Box
            role="grid"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {WEEKDAYS.map((weekday) => (
              <Typography
                key={weekday}
                role="columnheader"
                level="body-xs"
                sx={{
                  p: 0.5,
                  textAlign: "center",
                  bgcolor: "background.level1",
                }}
              >
                {weekday}
              </Typography>
            ))}
            {days.map((day) => {
              const key = dayKey(day);
              const events = eventsByDay.get(key) || [];
              const shown =
                view === "month"
                  ? events.slice(0, MAX_EVENTS_PER_DAY_IN_MONTH)
                  : events;
              const outside =
                view === "month" && day.getMonth() !== cursor.getMonth();
              return (
                <Box
                  key={key}
                  role="gridcell"
                  data-testid={`day-${key}`}
                  sx={{
                    minHeight: view === "week" ? 160 : 88,
                    p: 0.5,
                    border: "1px solid",
                    borderColor: "divider",
                    opacity: outside ? 0.55 : 1,
                    bgcolor: key === todayKey ? "primary.softBg" : undefined,
                  }}
                >
                  <Typography level="body-xs" sx={{ mb: 0.5 }}>
                    {day.getDate()}
                  </Typography>
                  {shown.map((event) => (
                    <Box
                      key={`${key}-${event.id}`}
                      onClick={
                        clickable ? () => openEvent(event.id) : undefined
                      }
                      sx={{
                        px: 0.5,
                        mb: 0.25,
                        borderRadius: "xs",
                        bgcolor: "primary.softBg",
                        cursor: clickable ? "pointer" : "default",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography level="body-xs">{event.title}</Typography>
                    </Box>
                  ))}
                  {events.length > shown.length && (
                    <Typography level="body-xs" sx={{ opacity: 0.7 }}>
                      +{events.length - shown.length} more
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
          {skipped > 0 && (
            <Typography level="body-xs" sx={{ mt: 1 }}>
              {skipped === 1
                ? "1 event without a usable date is not shown"
                : `${skipped} events without a usable date are not shown`}
            </Typography>
          )}
          {hasMore && totalCount !== undefined && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <Typography level="body-sm">
                Showing {rows.length} of {totalCount} events
              </Typography>
              <Button
                size="sm"
                variant="outlined"
                loading={isLoadingMore}
                onClick={loadMore}
              >
                Load more
              </Button>
            </Box>
          )}
        </>
      )}
    </Sheet>
  );
}

export default CanvasCalendarRenderer;
